import React, { useContext, useState, useEffect } from 'react';
import { HeadCell } from '../../../../graphics/tables/data/HeadCell';
import { TableRowData } from '../../../../graphics/tables/data/TableRowData';
import { DynamicDatatable, EditOptionType } from '../../../../graphics/tables/DynamicDatatable';
import { Order } from '../../../../graphics/tables/operations/sort';
import { strings } from '../../i18n/strings';
import { Page } from '../../presenters/pages/index';
import { AppContext, AppContextSpec, useAsyncEffect } from '../../App';
import { EditForm as DgCompliantPpsEditForm, EditFormModalOptions } from '../../domain/forms/DgCompliantPpsEditForm';
import { useOktaAuth } from '../../../../okta-react';
import { DgCompliantPpsTableStructure } from "../../domain/indexes/DgCompliantPpsIndexes";
import {
    deleteDgCompliantPps,
    getDgCompliantPpsColumnInformation,
    searchDgCompliantPps,
    editDgCompliantPps,
    createDgCompliantPps
} from "../../domain/repository/DgCompliantPpsRepository";
import {createStockPoolThreshold, editStockPoolThreshold} from "../../domain/repository/ThresholdRepository";

// Interfaces & types
type Record = DgCompliantPpsTableStructure.RecordOf<DgCompliantPpsTableStructure.DgCompliantPps>;
interface RecordRepresentation extends Record, TableRowData { }
export interface RecordData { record: RecordRepresentation, type: EditMode }

const tableFields: Array<string> = [
    'id',
    'channel',
    'store',
    'dgCompliantPps'
];

export enum EditMode {
    Add,
    Edit,
    Duplicate
}

const wordingDgCompliantPpsPage = strings.page.dgCompliantPps;
// const wordingDgCompliantPpsAuditPage = strings.page.dgCompliantPpsAudit;

export const DgCompliantPpsPage = () => {

    const DEFAULT_PAGINATION_LIMIT: number = (process.env.REACT_APP_DEFAULT_PAGINATION_LIMIT)
        ? parseInt(process.env.REACT_APP_DEFAULT_PAGINATION_LIMIT) : 20;

    const [headCellsConfig, setHeadCellsConfig] = useState<HeadCell<RecordRepresentation>[]>([]);
    const [rowData, setRowData] = useState<RecordRepresentation[]>([]);
    const [tableData, setTableData] = useState<DgCompliantPpsTableStructure.ResponseOf<DgCompliantPpsTableStructure.DgCompliantPps>>();
    const [tableDataDefinition, setTableDataDefinition] = useState<DgCompliantPpsTableStructure.ResponseOfColumn<DgCompliantPpsTableStructure.DgCompliantPps>>();
    const { authState } = useOktaAuth();
    const appContext = useContext<AppContextSpec>(AppContext);

    const wordingsDatatable = strings.datatable;
    const [limit, setLimit] = useState(DEFAULT_PAGINATION_LIMIT);
    const [page, setPage] = useState(1);
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<string>(tableFields[0]);
    const [recordToEdit, setRecordToEdit] = useState<RecordData>();
    const [editModalOpened, setEditModalOpened] = useState(false);

    // const currentPage = wordingDgCompliantPpsAuditPage.title;

    const headCells: HeadCell<RecordRepresentation>[] = tableFields.map((field) => ({
        id: field,
        label: field,
        displayName: field,
        type: "",
        required: true,
        information: "",
        position: 1,
        visible: true,
        editable: true,
        massEditable: false,
        reference: "",
        referenceColumnLabel: "",
        disablePadding: false,
        searchValue: "",
    }));

    useEffect(() => {
        (async () => {
            try {
                const headCellConfigValues: DgCompliantPpsTableStructure.ResponseOfColumn<DgCompliantPpsTableStructure.DgCompliantPps> =
                    await getDgCompliantPpsColumnInformation<DgCompliantPpsTableStructure.DgCompliantPps>(authState!);
                headCells.forEach((cell) => {
                    const config = headCellConfigValues[cell.id];
                    cell.id = config.name;
                    cell.label = config.name;
                    cell.type = config.type;
                    cell.required = config.required;
                    cell.information = config.information;
                    cell.position = config.position;
                    cell.visible = config.visible;
                    cell.editable = config.editable;
                    cell.massEditable = config.massEditable;
                    cell.displayName = (config.displayName.length > 0) ? String(config.displayName) : String(config.name);
                });
                setHeadCellsConfig(headCells);
                setTableDataDefinition(headCellConfigValues);
            } catch (error: any) {
                console.log("ERROR: ", error);
            }
        })();
        // eslint-disable-next-line
    }, []);

    const getAndRefreshRecords = (newList?: HeadCell<RecordRepresentation>[]) => {

        let headCellsConfigForGetAndRefresh = (newList) ? newList : headCellsConfig;

        let formattedSearchValues: DgCompliantPpsTableStructure.FormattedSearchValue[] = headCellsConfigForGetAndRefresh
            .filter(cell => cell.label && cell.searchValue)
            .map(cell => ({
                key: cell.label,
                value: cell.searchValue
            }));

        (async () => {
            try {
                const dgCompliantPpsValues: DgCompliantPpsTableStructure.ResponseOf<DgCompliantPpsTableStructure.DgCompliantPps> =
                    await searchDgCompliantPps<Record>(authState!, formattedSearchValues, page, limit, order, orderBy);

                if (page !== dgCompliantPpsValues.pagination.page) {
                    setPage(dgCompliantPpsValues.pagination.page);
                }
                setTableData(dgCompliantPpsValues);
                setRowData(dgCompliantPpsValues.values);
            } catch (error: any) {
                console.log("ERROR: ", error);
            }
        })();
    };

    useAsyncEffect(async () => {
        await getAndRefreshRecords();
    }, [page, limit, order, orderBy]);

    const handlePaginationChange = (page: number, limit: number) => {
        setPage(page);
        setLimit(limit);
    };

    const handleSortChange = async (order: Order, orderBy: string) => {
        setOrder(order);
        setOrderBy(orderBy);
    };

    const handleMultiCellsSearch = (newList: HeadCell<RecordRepresentation>[]) => {
        setPage(1);
        headCellsConfig.forEach((cell: HeadCell<RecordRepresentation>) => {
            cell.searchValue = newList.filter(newListCell => newListCell.id === cell.id)[0].searchValue;
        });
        getAndRefreshRecords(headCellsConfig);
    };

    const onChangeVisibilityColumnDialog = (newList: HeadCell<RecordRepresentation>[]) => {
        setHeadCellsConfig(newList.slice());
    };

    const onReorderDialogueAction = (newList: HeadCell<RecordRepresentation>[]) => {
        setHeadCellsConfig(newList.slice());
    };

    const fillAndOpenEditForm = (record?: RecordData) => {
        console.log(record)
        setRecordToEdit(record);
        setEditModalOpened(true);
    };

    const computeAddOption = () => ({
        buttonLabel: wordingDgCompliantPpsPage.datatable.action.add.buttonLabel,
        onClick: fillAndOpenEditForm,
    });

    const handleEditOptionClick = (option: EditOptionType, record: RecordRepresentation) => {
        switch (option) {
            case EditOptionType.Edit:
                fillAndOpenEditForm({ record, type: EditMode.Edit });
                break;
            case EditOptionType.Duplicate:
                fillAndOpenEditForm({ record, type: EditMode.Duplicate });
                break;
            case EditOptionType.Delete:
                appContext.prompt(
                    wordingDgCompliantPpsPage.datatable.action.remove.confirmationPromptMessage,
                    (isConfirmed) => {
                        if (isConfirmed) {
                            appContext.displayLoader(true);
                            deleteDgCompliantPps(authState!, record.id)
                                .then(() => {
                                    getAndRefreshRecords();
                                    appContext.notify({ message: wordingDgCompliantPpsPage.datatable.action.remove.confirmedFeedbackMessage });
                                })
                                .catch(({ message }) => message && appContext.notifyError(message))
                                .finally(() => appContext.displayLoader(false));
                        }
                    },
                    { confirmButtonColor: 'secondary' }
                );
                break;
        }
    };

    const computeEditOptions = () => ([
        { id: EditOptionType.Edit, name: wordingDgCompliantPpsPage.datatable.action.edit.menuItemLabel, onClick: handleEditOptionClick },
        { id: EditOptionType.Duplicate, name: wordingDgCompliantPpsPage.datatable.action.duplicate.menuItemLabel, onClick: handleEditOptionClick },
        { id: EditOptionType.Delete, name: wordingDgCompliantPpsPage.datatable.action.remove.menuItemLabel, onClick: handleEditOptionClick },
    ]);

    const handleEditModalClose = () => {
        setEditModalOpened(false);
        setRecordToEdit(undefined);
    };

    const handleEditModalOk = (shouldReset: boolean) => {
        const feedbackWordings = recordToEdit
            ? (recordToEdit.type === EditMode.Edit ? wordingDgCompliantPpsPage.datatable.action.edit : wordingDgCompliantPpsPage.datatable.action.duplicate)
            : wordingDgCompliantPpsPage.datatable.action.add;
        !shouldReset && setEditModalOpened(false);

        (async () => {
            try {
                getAndRefreshRecords();
                appContext.notifySuccess(feedbackWordings.confirmationFeedbackMessage);
            } catch (error: any) {
                appContext.notifyError(error);
            }
        })();
    };

    const computeEditModalOptions = (): EditFormModalOptions => {
        const modalWordings = recordToEdit
            ? (recordToEdit?.type === EditMode.Duplicate
                ? wordingDgCompliantPpsPage.datatable.action.duplicate.modal
                : wordingDgCompliantPpsPage.datatable.action.edit.modal)
            : wordingDgCompliantPpsPage.datatable.action.add.modal;

        return {
            title: modalWordings.title,
            applyLabel: modalWordings.applyButtonLabel,
            cancelLabel: modalWordings.cancelButtonLabel,
            applyAndResetLabel: recordToEdit
                ? (recordToEdit.type === EditMode.Duplicate ? wordingDgCompliantPpsPage.datatable.action.duplicate.modal.applyAndResetLabel : undefined)
                : wordingDgCompliantPpsPage.datatable.action.add.modal.applyAndResetLabel,
            open: editModalOpened,
            onClose: handleEditModalClose,
            onSuccess: async (shouldReset: boolean) => {
                await handleEditModalOk(shouldReset);
            }
        };
    };

    const handleEditFormSubmit = async (form: Record) => {
        const feedbackWordings = recordToEdit ? (recordToEdit.type === EditMode.Edit ? wordingDgCompliantPpsPage.datatable.action.edit : wordingDgCompliantPpsPage.datatable.action.duplicate) : wordingDgCompliantPpsPage.datatable.action.add;
        await (recordToEdit
            ? (recordToEdit.type === EditMode.Edit ? editDgCompliantPps(authState!, recordToEdit.record.id, form) : createDgCompliantPps(authState!, form))
            : createDgCompliantPps(authState!, form));

        return feedbackWordings.confirmationFeedbackMessage;
    };

    return (
        <Page
            title={wordingDgCompliantPpsPage.title}
            descriptionLine1={wordingDgCompliantPpsPage.description.line1}
            descriptionLine2={wordingDgCompliantPpsPage.description.line2}
        >
            <>
                <DynamicDatatable
                    stickyHeader={true}
                    headCells={headCellsConfig}
                    data={rowData}
                    initialOrderBy={tableFields[0]}
                    showPadding={false}
                    editOptions={computeEditOptions()}
                    onChangeVisibilityColumnDialog={onChangeVisibilityColumnDialog}
                    onReorderDialogueAction={onReorderDialogueAction}
                    allowReordering={{
                        modal: {
                            title: wordingsDatatable.reorderColumns.modal.title,
                            applyButtonLabel: wordingsDatatable.reorderColumns.modal.applyButtonLabel,
                        },
                        triggerButtonLabel: wordingsDatatable.reorderColumns.modal.title
                    }}
                    allowChangeVisibilityColumn={{
                        modal: {
                            title: wordingsDatatable.changeVisibilityColumns.modal.title,
                            applyButtonLabel: wordingsDatatable.changeVisibilityColumns.modal.applyButtonLabel,
                        },
                        triggerButtonLabel: wordingsDatatable.changeVisibilityColumns.modal.title
                    }}
                    allowAdding={computeAddOption()}
                    paginationOptions={(() => ({
                        page,
                        limit,
                        order,
                        orderBy,
                        total: tableData?.pagination?.total || 0,
                        rowsPerPageValues: [
                            { nb: DEFAULT_PAGINATION_LIMIT / 2, isDefault: true },
                            { nb: DEFAULT_PAGINATION_LIMIT, isDefault: true },
                            { nb: DEFAULT_PAGINATION_LIMIT * 2, isDefault: false },
                            { nb: DEFAULT_PAGINATION_LIMIT * 4, isDefault: false },
                        ],
                        meetNbRowsPerPage: false,
                        rowsPerPageLabel: wordingsDatatable.pagination.rowsPerPage,
                        onPaginationChange: handlePaginationChange,
                        onHandleSortChange: handleSortChange,
                        onMultiCellsSearch: handleMultiCellsSearch
                    }))()}
                    // showHistory={[{
                    //     showHistoryButton: true,
                    //     urlToViewHistory: '/dg-compliant-pps-audit',
                    //     linkToParentPage: false,
                    //     icon: 'HistoryIcon'
                    // }]}
                    // pageTitles={[currentPage]}
                />
            </>
            {tableDataDefinition && (
                <DgCompliantPpsEditForm
                    key={recordToEdit ? recordToEdit.type : EditMode.Add}
                    record={recordToEdit ? recordToEdit.record : undefined}
                    constraints={tableDataDefinition}
                    onSubmit={handleEditFormSubmit}
                    modalOptions={computeEditModalOptions()}
                />
            )}
        </Page>
    );
};
